import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BusinessIcon from "@mui/icons-material/Business";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import {
  useCheckBanquetAvailabilityMutation,
  useGetCustomerQuery,
} from "../../../Api/attoDeskApi";
import AddressAutocomplete, { ParsedAddress } from "../../../Components/GoogleAddressAutocomplete";
import { useNotifier } from "../../../Core/Notifier";
import PremiumDatePicker from "../../../Components/PremiumDatePicker";
import { HALL_OPTIONS } from "../Pricing/constants";
import {
  BookingFormState,
  CustomerForm,
  deriveTimeSlot,
  EMPTY_CUSTOMER,
  EVENT_TYPES,
  fmtCAD,
  PROVINCES,
  SelectedHall,
} from "./bookingTypes";
import { useHallRecommendation } from "./useHallRecommendation";
import { calculateHallPrice } from "./hallRecommendation";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy:    "#0f172a",
  gold:    "#d4a373",
  cream:   "#f8f5f0",
  white:   "#ffffff",
  bg:      "#f8f5f0",
  slate:   "#6b7280",
  slateLt: "#9ca3af",
  line:    "#e5e7eb",
  doneBg:  "#f8f5f0",
  doneTx:  "#6b7280",
  doneBd:  "#d4a373",
  redTx:   "#9b2c2c",
  peach:   "#f8f5f0",
  inputBg: "#fafaf9",
} as const;

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION CARD — premium wrapper for each form section
   ═══════════════════════════════════════════════════════════════════════════ */
const SectionCard: React.FC<{
  icon: React.ReactNode; title: string; subtitle?: string;
  action?: React.ReactNode; children: React.ReactNode;
}> = ({ icon, title, subtitle, action, children }) => (
  <Box sx={{
    bgcolor: T.white, borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03), 0 4px 16px rgba(0,0,0,0.02)",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 2px 6px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.03)" },
  }}>
    {/* Header */}
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2.5, md: 3 }, pt: 2.5, pb: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: "10px", bgcolor: T.bg, display: "flex", alignItems: "center", justifyContent: "center", color: T.gold, flexShrink: 0 }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11, color: T.slateLt, mt: 0.2 }}>{subtitle}</Typography>}
        </Box>
      </Box>
      {action}
    </Box>
    {/* Body */}
    <Box sx={{ px: { xs: 2.5, md: 3 }, pt: 2, pb: 3 }}>
      {children}
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   INPUT STYLES
   ═══════════════════════════════════════════════════════════════════════════ */
const inputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13.5, borderRadius: "10px", bgcolor: T.inputBg,
    border: `1px solid ${T.line}`,
    "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold },
    transition: "all 0.15s ease",
    "& fieldset": { border: "none" },
  },
  "& .MuiInputLabel-root": { fontSize: 13 },
};
const reqSx = { ...inputSx, "& .MuiInputBase-root": { ...inputSx["& .MuiInputBase-root"], "&.Mui-error": { borderColor: "#e0a0a0" } } };

/* ═══════════════════════════════════════════════════════════════════════════
   PROPS & CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════ */
interface Props {
  form: BookingFormState;
  setForm: React.Dispatch<React.SetStateAction<BookingFormState>>;
  excludeBookingId?: number;
}

const BASE_HALLS = ["Nova", "Titan", "Lunar", "Eclipse"] as const;
type BaseHall = (typeof BASE_HALLS)[number];
const HALL_COLOR: Record<BaseHall, string> = { Nova: "#d4a373", Titan: "#d4a373", Lunar: "#d4a373", Eclipse: "#d4a373" };
const HALL_BG: Record<BaseHall, string> = { Nova: "#f8f5f0", Titan: "#f8f5f0", Lunar: "#f8f5f0", Eclipse: "#f8f5f0" };
const hallsInKey = (key: string): BaseHall[] => (BASE_HALLS as readonly string[]).filter(h => key.includes(h)) as BaseHall[];

// Adjacency — halls form a linear chain: Nova ↔ Titan ↔ Lunar ↔ Eclipse
const HALL_ORDER: BaseHall[] = ["Nova", "Titan", "Lunar", "Eclipse"];

function isContiguous(halls: BaseHall[]): boolean {
  if (halls.length <= 1) return true;
  const indices = halls.map(h => HALL_ORDER.indexOf(h)).sort((a, b) => a - b);
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] - indices[i - 1] !== 1) return false;
  }
  return true;
}

function deriveComboKey(halls: BaseHall[]): string {
  if (halls.length === 0) return "";
  if (halls.length === 4) return "Full Hall";
  const sorted = [...halls].sort((a, b) => HALL_ORDER.indexOf(a) - HALL_ORDER.indexOf(b));
  return sorted.join(" + ");
}

interface CustomerResult {
  id: number; customerFirstName: string; customerLastName: string;
  customerMobileNum: string; customerEmail: string; customerProvince: string;
  customerCity: string; customerPostalCode: string; customerStreetName: string;
  customerStreetNumber: string; customerCompanyName: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
const Step1EventDetails: React.FC<Props> = ({ form, setForm, excludeBookingId }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();

  const [occupied, setOccupied] = useState<Record<string, Record<string, any>>>({});
  const [rawPricing, setRawPricing] = useState<Record<string, any[]> | undefined>(undefined);
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [checkAvailability] = useCheckBanquetAvailabilityMutation();
  const [manualOverride, setManualOverride] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showAddress, setShowAddress] = useState(false);
  const [showSecondary, setShowSecondary] = useState(form.hasSecondaryCustomer);
  const [contactType, setContactType] = useState<"individual" | "corporate">("individual");

  useEffect(() => { if (form.primaryCustomer.organization) setContactType("corporate"); }, []);
  useEffect(() => { if (form.primaryCustomer.address || form.primaryCustomer.city) setShowAddress(true); }, []);

  const doCheckAvail = useCallback(async () => {
    if (!form.eventDate || !form.startTime || !form.endTime) return;
    setCheckingAvail(true);
    try {
      const res = (await checkAvailability({ date: form.eventDate }).unwrap()) as any;
      setOccupied(res.status && res.data?.occupied ? res.data.occupied : {});
      setRawPricing(res.status && res.data?.pricing ? res.data.pricing : undefined);
    }
    catch { setOccupied({}); setRawPricing(undefined); }
    finally { setCheckingAvail(false); }
  }, [form.eventDate, form.startTime, form.endTime, checkAvailability]);
  useEffect(() => { doCheckAvail(); }, [doCheckAvail]);

  useEffect(() => {
    if (searchQuery.length < 2) { setDebouncedSearch(""); return; }
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: customerData, isFetching: isSearching } = useGetCustomerQuery({ searchText: debouncedSearch, id: "" }, { skip: debouncedSearch.length < 2 });
  const searchResults: CustomerResult[] = useMemo(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) return [];
    const raw = (customerData as any)?.data;
    return Array.isArray(raw) ? raw.slice(0, 8) : [];
  }, [customerData, debouncedSearch]);

  const handleSelectCustomer = (c: CustomerResult | null) => {
    if (!c) return;
    setSearchQuery(""); setDebouncedSearch("");
    setForm(p => ({ ...p, primaryCustomer: { ...p.primaryCustomer,
      firstName: c.customerFirstName || "", lastName: c.customerLastName || "",
      organization: c.customerCompanyName || "", email1: c.customerEmail || "",
      phone1: c.customerMobileNum || "",
      address: [c.customerStreetNumber, c.customerStreetName].filter(Boolean).join(" "),
      city: c.customerCity || "", province: c.customerProvince || "ON",
      postalCode: c.customerPostalCode || "",
    }}));
    if (c.customerCompanyName) setContactType("corporate");
    if (c.customerCity || c.customerStreetName) setShowAddress(true);
  };

  // Extract event name from occupied slot (handles both string and object format)
  const slotLabel = (val: any): string | null => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.event) return val.event;
    return "Booked";
  };

  const isHallOccupied = (hallKey: string): string | null => {
    const halls = hallsInKey(hallKey); const slot = form.timeSlot;
    for (const h of halls) { const info = occupied[h]; if (!info) continue;
      if (slot === "FULL_DAY") {
        const v = slotLabel(info["AM"]) || slotLabel(info["PM"]) || slotLabel(info["FULL_DAY"]);
        if (v) return v;
      }
      const v = slotLabel(info[slot]) || slotLabel(info["FULL_DAY"]);
      if (v) return v;
    } return null;
  };

  // Get conflict detail (slot label) for display
  const getConflictDetail = (hallKey: string): { event: string; slot: string } | null => {
    const halls = hallsInKey(hallKey); const slot = form.timeSlot;
    for (const h of halls) { const info = occupied[h]; if (!info) continue;
      for (const s of slot === "FULL_DAY" ? ["AM", "PM", "FULL_DAY"] : [slot, "FULL_DAY"]) {
        const val = info[s];
        if (val) {
          const event = typeof val === "object" ? val.event : val;
          const slotStr = typeof val === "object" ? val.slot : s;
          return { event: event || "Booked", slot: slotStr || s };
        }
      }
    }
    return null;
  };

  // ── Smart hall recommendation engine ──
  const guestCount = Number(form.expectedGuests) || 0;
  const { recommendations, bestPick, cheapestPick, savings, hasPricing, pricingMap, capacityFallback } =
    useHallRecommendation({ guestCount, timeSlot: form.timeSlot, occupied, rawPricing });

  // Derive which individual base halls are currently selected from the combo key
  const selectedBaseHalls: BaseHall[] = useMemo(() => {
    if (form.halls.length === 0) return [];
    const key = form.halls[0].hallKey;
    if (key === "Full Hall") return [...HALL_ORDER];
    return hallsInKey(key);
  }, [form.halls]);

  // Apply a new set of base halls → derive combo key → update form
  const applyHallSelection = (baseHalls: BaseHall[]) => {
    if (baseHalls.length === 0) {
      setForm(p => ({ ...p, halls: [], hallFlatFee: "0", hallPricePerPerson: "0" }));
      return;
    }
    const comboKey = deriveComboKey(baseHalls);
    const hallOption = HALL_OPTIONS.find(o => o.key === comboKey);
    if (!hallOption) {
      setForm(p => ({ ...p, halls: [], hallFlatFee: "0", hallPricePerPerson: "0" }));
      return;
    }
    const pricing = pricingMap[comboKey];
    setForm(p => ({
      ...p,
      halls: [{ hallKey: comboKey, hallLabel: comboKey, hallCapacity: hallOption.capacity }],
      ...(pricing
        ? { hallFlatFee: String(pricing.flatFee), hallPricePerPerson: String(pricing.pricePerPerson) }
        : { hallFlatFee: "0", hallPricePerPerson: "0" }),
    }));
  };

  const handleSelectHall = (opt: (typeof HALL_OPTIONS)[number]) => {
    setManualOverride(true);
    if (opt.type === "single") {
      const hallName = opt.key as BaseHall;
      if (selectedBaseHalls.includes(hallName)) {
        // Deselect — keep remaining if still contiguous, otherwise clear all
        const remaining = selectedBaseHalls.filter(h => h !== hallName);
        applyHallSelection(isContiguous(remaining) ? remaining : []);
      } else {
        // Check availability first
        if (isHallOccupied(hallName)) {
          showErrorMessage(`${hallName} is booked for this time slot`);
          return;
        }
        const newSelection = [...selectedBaseHalls, hallName] as BaseHall[];
        // If contiguous → add to selection; otherwise start fresh with just this hall
        applyHallSelection(isContiguous(newSelection) ? newSelection : [hallName]);
      }
    } else {
      // Combo card click
      const comboHalls: BaseHall[] = opt.key === "Full Hall" ? [...HALL_ORDER] : hallsInKey(opt.key);
      if (form.halls[0]?.hallKey === opt.key) {
        // Already selected — deselect all
        applyHallSelection([]);
      } else {
        // Check availability of every hall in the combo
        for (const h of comboHalls) {
          if (isHallOccupied(h)) {
            showErrorMessage(`${h} is booked for this time slot`);
            return;
          }
        }
        applyHallSelection(comboHalls);
      }
    }
  };

  const handleSelectRecommendation = (rec: typeof recommendations[number]) => {
    setManualOverride(true);
    const recHalls: BaseHall[] = rec.hallKey === "Full Hall" ? [...HALL_ORDER] : hallsInKey(rec.hallKey);
    for (const h of recHalls) {
      if (isHallOccupied(h)) {
        showErrorMessage(`${h} is booked for this time slot`);
        return;
      }
    }
    applyHallSelection(recHalls);
  };

  // Auto-select best hall when inputs change (unless user manually picked)
  useEffect(() => {
    if (manualOverride) return;
    if (guestCount <= 0 || !form.eventDate || !form.startTime || !form.endTime) return;

    if (hasPricing && bestPick && !isHallOccupied(bestPick.hallKey)) {
      const rawPricing = pricingMap[bestPick.hallKey];
      setForm(p => ({ ...p, halls: [{ hallKey: bestPick.hallKey, hallLabel: bestPick.hallKey, hallCapacity: bestPick.capacity }],
        hallFlatFee: String(rawPricing?.flatFee ?? bestPick.flatFee), hallPricePerPerson: String(rawPricing?.pricePerPerson ?? bestPick.pricePerPerson) }));
    } else if (capacityFallback && !isHallOccupied(capacityFallback.key)) {
      const switched = hasPricing && bestPick && isHallOccupied(bestPick.hallKey);
      if (switched) showMessage(`${bestPick!.hallKey} unavailable — switched to ${capacityFallback.key}`);
      setForm(p => ({ ...p, halls: [{ hallKey: capacityFallback.key, hallLabel: capacityFallback.key, hallCapacity: capacityFallback.capacity }] }));
    } else {
      // All preferred halls occupied — find first available
      const available = HALL_OPTIONS.find(h => h.capacity >= guestCount && !isHallOccupied(h.key));
      if (available) {
        showMessage(`Preferred halls unavailable — switched to ${available.key}`);
        setForm(p => ({ ...p, halls: [{ hallKey: available.key, hallLabel: available.key, hallCapacity: available.capacity }] }));
      } else {
        setForm(p => ({ ...p, halls: [] }));
      }
    }
  }, [guestCount, bestPick?.hallKey, capacityFallback?.key, hasPricing, form.eventDate, form.startTime, form.endTime, manualOverride, occupied]);

  // Reset manual override when date/time/guests change
  useEffect(() => { setManualOverride(false); }, [form.eventDate, form.startTime, form.endTime, form.expectedGuests]);

  const setCust = (type: "primaryCustomer" | "secondaryCustomer") => (field: keyof CustomerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [type]: { ...p[type], [field]: e.target.value } }));
  const setPrimary = setCust("primaryCustomer");
  const setSecondary = setCust("secondaryCustomer");

  // Phone formatting: digits only, max 10, auto-format XXX XXX XXXX
  const formatPhone = (raw: string): string => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  };
  const setPhone = (type: "primaryCustomer" | "secondaryCustomer", field: keyof CustomerForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const formatted = formatPhone(e.target.value);
      setForm(p => ({ ...p, [type]: { ...p[type], [field]: formatted } }));
    };
  const phoneDigits = (val: string) => val.replace(/\D/g, "").length;
  const phoneError = (val: string) => val.length > 0 && phoneDigits(val) < 10 ? "Enter valid 10-digit number" : "";

  const actionBtn = (label: string, icon: React.ReactNode, active: boolean, onClick: () => void) => (
    <Button size="small" startIcon={active ? <ExpandLessIcon /> : icon} onClick={onClick}
      sx={{ textTransform: "none", fontSize: 12, fontWeight: 500, color: T.slate, borderRadius: "8px", px: 1.5, py: 0.6, bgcolor: active ? T.peach : T.bg, border: `1px solid ${active ? T.gold : "transparent"}`, "&:hover": { bgcolor: T.peach, borderColor: T.gold } }}>
      {label}
    </Button>
  );

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 3.5 } }}>

      {/* ══════════ EVENT INFORMATION ══════════ */}
      <SectionCard icon={<EventOutlinedIcon sx={{ fontSize: 17 }} />} title="Event Information" subtitle="Basic event details and scheduling">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField label="Banquet / Event Name" fullWidth size="small" value={form.banquetName} onChange={e => setForm(p => ({ ...p, banquetName: e.target.value }))} sx={inputSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Agent / Coordinator" fullWidth size="small" value={form.agentName} onChange={e => setForm(p => ({ ...p, agentName: e.target.value }))} sx={inputSx} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Event Type *" fullWidth size="small" value={form.eventType} onChange={e => setForm(p => ({ ...p, eventType: e.target.value }))} error={!form.eventType} sx={reqSx}>
              {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Number of Guests *"
              fullWidth
              size="small"
              type="number"
              value={form.expectedGuests}
              onChange={e => setForm(p => ({ ...p, expectedGuests: e.target.value }))}
              error={!form.expectedGuests || Number(form.expectedGuests) <= 0}
              InputProps={{
                inputProps: { min: 1, max: 900 },
              }}
              sx={reqSx}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <PremiumDatePicker
              label="Event Date *"
              value={form.eventDate}
              onChange={(v) => setForm(p => ({ ...p, eventDate: v, halls: [] }))}
              error={!form.eventDate}
              size="small"
              sx={reqSx}
              minDate={new Date().toISOString().split("T")[0]}
            />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField
              label="Start Time *"
              fullWidth
              size="small"
              type="time"
              value={form.startTime}
              onChange={e => {
                const startTime = e.target.value;
                setForm(p => ({ ...p, startTime, timeSlot: deriveTimeSlot(startTime, p.endTime), halls: [] }));
              }}
              error={!form.startTime}
              sx={reqSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={1.5}>
            <TextField
              label="End Time *"
              fullWidth
              size="small"
              type="time"
              value={form.endTime}
              onChange={e => {
                const endTime = e.target.value;
                setForm(p => ({ ...p, endTime, timeSlot: deriveTimeSlot(p.startTime, endTime), halls: [] }));
              }}
              error={!form.endTime}
              sx={reqSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* ══════════ HALL SELECTION ══════════ */}
      <SectionCard
        icon={<MeetingRoomOutlinedIcon sx={{ fontSize: 17 }} />}
        title="Hall Selection"
        subtitle="Choose a venue for your event"
        action={
          form.halls.length > 0 ? (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={(() => {
                const p = pricingMap[form.halls[0].hallKey];
                if (p && guestCount > 0) {
                  const calc = calculateHallPrice({ flatFee: p.flatFee, perPersonPrice: p.pricePerPerson, guestCount, capacity: form.halls[0].hallCapacity });
                  return `${form.halls[0].hallLabel} — ${form.halls[0].hallCapacity} pax · ${fmtCAD(calc.total)}`;
                }
                return `${form.halls[0].hallLabel} — ${form.halls[0].hallCapacity} pax`;
              })()}
              sx={{ bgcolor: T.doneBg, color: T.doneTx, border: `1px solid ${T.doneBd}`, fontWeight: 600, fontSize: 11, height: 26, borderRadius: "8px" }} />
          ) : checkingAvail ? <Typography sx={{ fontSize: 11, color: T.slateLt }}>Checking...</Typography> : null
        }
      >
        {(!form.eventDate || !form.startTime || !form.endTime) && (
          <Alert severity="info" sx={{ mb: 3, fontSize: 13, borderRadius: "12px", border: "none", bgcolor: T.bg, "& .MuiAlert-icon": { color: T.slateLt } }}>
            Select a date and time slot to check hall availability.
          </Alert>
        )}

        {checkingAvail && form.eventDate && form.startTime && form.endTime && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, p: 1.5, bgcolor: T.peach, borderRadius: "10px" }}>
            <Box sx={{ width: 16, height: 16, border: "2px solid", borderColor: `${T.gold} transparent ${T.gold} transparent`, borderRadius: "50%", animation: "spin 0.8s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.navy }}>Checking availability for {new Date(form.eventDate + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" })}...</Typography>
          </Box>
        )}

        {/* ── Pricing Recommendations ── */}
        {recommendations.length > 0 && guestCount > 0 && !checkingAvail && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>
              Recommended for {guestCount} guests
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${Math.min(recommendations.length, 3)}, 1fr)` }, gap: 1.5 }}>
              {recommendations.slice(0, 3).map((rec) => {
                const isSelected = form.halls[0]?.hallKey === rec.hallKey;
                const isBest = rec.tag === "best_value";
                const isCheapest = rec.tag === "cheapest";
                const isPremium = rec.tag === "premium";
                const hasTag = isBest || isCheapest || isPremium;
                const tagColor = isBest ? T.gold : isCheapest ? "#3b82f6" : "#6a1b9a";
                const tagLabel = isBest ? "Best Fit" : isCheapest ? "Best Value" : "Premium";
                const tagIcon = isBest ? <LocalFireDepartmentIcon sx={{ fontSize: 11 }} /> : isCheapest ? <SavingsOutlinedIcon sx={{ fontSize: 11 }} /> : <StarIcon sx={{ fontSize: 11 }} />;
                const subtext = isBest
                  ? `Perfect fit for ${guestCount} guests`
                  : isCheapest
                    ? "Most cost-effective option"
                    : isPremium
                      ? "Spacious premium setup"
                      : "";

                return (
                  <Box
                    key={rec.hallKey}
                    onClick={() => handleSelectRecommendation(rec)}
                    sx={{
                      position: "relative", p: 2, borderRadius: "14px", cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      bgcolor: isSelected ? T.navy : T.white,
                      border: `2px solid ${isSelected ? T.navy : isBest ? T.gold : T.line}`,
                      boxShadow: isSelected
                        ? `0 4px 20px rgba(13,27,42,0.18)`
                        : "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.02)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        borderColor: isSelected ? T.navy : T.gold,
                        boxShadow: isSelected
                          ? "0 6px 24px rgba(13,27,42,0.22)"
                          : `0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px ${T.gold}30`,
                      },
                    }}
                  >
                    {/* Tag badge */}
                    {hasTag && (
                      <Box sx={{
                        position: "absolute", top: -11, left: 12,
                        display: "flex", alignItems: "center", gap: 0.4,
                        bgcolor: tagColor, color: T.white,
                        px: 1, py: "3px", borderRadius: "6px",
                        fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                        boxShadow: `0 2px 6px ${tagColor}40`,
                      }}>
                        {tagIcon}
                        {tagLabel}
                      </Box>
                    )}

                    {/* Hall name */}
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: isSelected ? T.white : T.navy, lineHeight: 1.2, mt: hasTag ? 0.5 : 0 }}>
                      {rec.hallKey}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: isSelected ? "rgba(255,255,255,0.6)" : T.slateLt, mt: 0.3 }}>
                      {rec.capacity} guests capacity
                    </Typography>

                    {/* Subtext */}
                    {subtext && (
                      <Typography sx={{ fontSize: 10, fontWeight: 500, color: isSelected ? "rgba(255,255,255,0.5)" : T.slate, mt: 0.5, fontStyle: "italic" }}>
                        {subtext}
                      </Typography>
                    )}

                    {/* Price */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 1.2 }}>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: isSelected ? T.gold : T.navy, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                        {fmtCAD(rec.totalPrice)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 10, color: isSelected ? "rgba(255,255,255,0.5)" : T.slateLt, mt: 0.3 }}>
                      {rec.flatFee > 0
                        ? `${fmtCAD(rec.flatFee)} + ${fmtCAD(rec.pricePerPerson)}/pp`
                        : `${fmtCAD(rec.pricePerPerson)}/pp`}
                    </Typography>

                    {/* Savings badge */}
                    {isCheapest && savings > 0 && (
                      <Box sx={{ mt: 1, display: "inline-flex", alignItems: "center", gap: 0.3, bgcolor: isSelected ? "rgba(255,255,255,0.12)" : "#ecfdf5", px: 0.8, py: "2px", borderRadius: "6px" }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: isSelected ? "#86efac" : "#16a34a" }}>
                          Save {fmtCAD(savings)}
                        </Typography>
                      </Box>
                    )}

                    {/* Selected indicator */}
                    {isSelected && (
                      <Box sx={{ position: "absolute", top: 10, right: 10, width: 22, height: 22, borderRadius: "50%", bgcolor: T.gold, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: T.navy }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── No valid combination warning ── */}
        {guestCount > 0 && form.eventDate && form.startTime && form.endTime && !checkingAvail && recommendations.length === 0 && hasPricing && (
          <Alert severity="warning" sx={{ mb: 3, fontSize: 12, borderRadius: "12px" }}>
            No hall combination available for {guestCount} guests on this date/slot. Try a different date or time.
          </Alert>
        )}

        {/* ── Single Halls ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>
          Individual Halls
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" }, gap: { xs: 1.5, sm: 2 }, mb: 4 }}>
          {HALL_OPTIONS.filter(h => h.type === "single").map(opt => {
            const blocker = isHallOccupied(opt.key);
            const selected = selectedBaseHalls.includes(opt.key as BaseHall);
            const color = HALL_COLOR[opt.key as BaseHall];
            const bg = HALL_BG[opt.key as BaseHall];
            const capacityExceeded = guestCount > 0 && guestCount > opt.capacity && !selected;
            const disabled = (!!blocker && !selected) || capacityExceeded;
            return (
              <Tooltip key={opt.key} title={blocker ? `Unavailable — ${blocker}` : `Select ${opt.key} — ${opt.capacity} guests`} arrow>
                <Box
                  onClick={() => !disabled && handleSelectHall(opt)}
                  sx={{
                    position: "relative", borderRadius: "14px", overflow: "hidden",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.45 : 1,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: `2px solid ${selected ? color : "transparent"}`,
                    boxShadow: selected
                      ? `0 0 0 1px ${color}, 0 4px 16px ${color}22`
                      : "0 1px 3px rgba(0,0,0,0.04), 0 2px 8px rgba(0,0,0,0.02)",
                    bgcolor: selected ? bg : T.white,
                    "&:hover": !disabled ? {
                      transform: "translateY(-2px)",
                      boxShadow: selected
                        ? `0 0 0 1px ${color}, 0 8px 24px ${color}28`
                        : "0 4px 12px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)",
                      borderColor: selected ? color : T.line,
                    } : {},
                  }}
                >
                  {/* Color accent bar */}
                  <Box sx={{ height: 4, bgcolor: selected ? color : T.line, transition: "background 0.2s" }} />

                  <Box sx={{ px: 2, pt: 2, pb: 2, textAlign: "center" }}>
                    {/* Hall icon / initial */}
                    <Box sx={{
                      width: 44, height: 44, borderRadius: "12px", mx: "auto", mb: 1.2,
                      bgcolor: selected ? `${color}18` : T.bg,
                      border: `1.5px solid ${selected ? `${color}40` : T.line}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s",
                    }}>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: selected ? color : T.slateLt, transition: "color 0.2s" }}>
                        {opt.key.charAt(0)}
                      </Typography>
                    </Box>

                    {/* Name */}
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: selected ? color : T.navy, lineHeight: 1.2, mb: 0.3 }}>
                      {opt.key}
                    </Typography>

                    {/* Capacity */}
                    <Typography sx={{ fontSize: 11, color: T.slateLt, fontWeight: 500 }}>
                      {opt.capacity} guests
                    </Typography>

                    {/* Pricing */}
                    {pricingMap[opt.key] && !blocker && (() => {
                      const p = pricingMap[opt.key];
                      const exceeds = guestCount > opt.capacity;
                      if (exceeds && guestCount > 0) {
                        return <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.redTx, mt: 0.5 }}>Capacity exceeded</Typography>;
                      }
                      if (guestCount > 0) {
                        const calc = calculateHallPrice({ flatFee: p.flatFee, perPersonPrice: p.pricePerPerson, guestCount, capacity: opt.capacity });
                        return (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? color : T.navy }}>{fmtCAD(calc.total)}</Typography>
                            <Typography sx={{ fontSize: 9, color: selected ? `${color}aa` : T.slateLt }}>
                              {fmtCAD(p.flatFee)} + {fmtCAD(p.pricePerPerson)}/pp
                            </Typography>
                          </Box>
                        );
                      }
                      return (
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: selected ? color : T.slate, mt: 0.5 }}>
                          {fmtCAD(p.flatFee)} + {fmtCAD(p.pricePerPerson)}/pp
                        </Typography>
                      );
                    })()}

                    {/* Recommendation badge */}
                    {!blocker && (() => {
                      const rec = recommendations.find(r => r.hallKey === opt.key);
                      if (!rec?.tag) return null;
                      const isBestFit = rec.tag === "best_value";
                      return (
                        <Box sx={{ mt: 0.5, display: "inline-flex", alignItems: "center", gap: 0.3, bgcolor: isBestFit ? `${T.gold}18` : "#edf7ee", px: 0.8, py: "2px", borderRadius: "6px" }}>
                          <Typography sx={{ fontSize: 8, fontWeight: 700, color: isBestFit ? T.gold : "#2d6a35", textTransform: "uppercase" }}>
                            {isBestFit ? (guestCount > 0 ? `Best for ${guestCount}` : "Best Fit") : "Best Value"}
                          </Typography>
                        </Box>
                      );
                    })()}

                    {/* Status indicator — rich conflict info */}
                    {blocker && (() => {
                      const conflict = getConflictDetail(opt.key);
                      return (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, bgcolor: "#fef2f2", px: 0.8, py: 0.3, borderRadius: "6px", border: "1px solid #fecaca" }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: T.redTx, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 9, color: T.redTx, fontWeight: 700 }}>Booked</Typography>
                          </Box>
                          {conflict && (
                            <Typography sx={{ fontSize: 8, color: T.slateLt, mt: 0.3, lineHeight: 1.3 }} noWrap>
                              {conflict.event} ({conflict.slot})
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>

                  {/* Selected checkmark */}
                  {selected && (
                    <Box sx={{
                      position: "absolute", top: 10, right: 10,
                      width: 22, height: 22, borderRadius: "50%",
                      bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: `0 2px 6px ${color}40`,
                    }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: T.white }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* ── Divider ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: T.line }} />
          <Typography sx={{ fontSize: 9, fontWeight: 600, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.1em" }}>or combine</Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: T.line }} />
        </Box>

        {/* ── Combinations ── */}
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.1em", mb: 2 }}>
          Hall Combinations
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: { xs: 1.2, sm: 1.5 } }}>
          {HALL_OPTIONS.filter(h => h.type === "combo").map(opt => {
            const blocker = isHallOccupied(opt.key);
            const selected = form.halls[0]?.hallKey === opt.key;
            const capacityExceeded = guestCount > 0 && guestCount > opt.capacity && !selected;
            const disabled = (!!blocker && !selected) || capacityExceeded;
            const isFullHall = opt.key === "Full Hall";
            return (
              <Tooltip key={opt.key} title={blocker ? `Unavailable — ${blocker}` : `${opt.capacity} guests`} arrow>
                <Box
                  onClick={() => !disabled && handleSelectHall(opt)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2, py: 1.5, borderRadius: "12px",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.4 : 1,
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    bgcolor: selected ? T.navy : T.white,
                    border: `1.5px solid ${selected ? T.navy : isFullHall ? T.gold : T.line}`,
                    boxShadow: selected
                      ? "0 2px 8px rgba(13,27,42,0.15)"
                      : "0 1px 2px rgba(0,0,0,0.03)",
                    "&:hover": !disabled ? {
                      transform: "translateY(-1px)",
                      borderColor: selected ? T.navy : T.gold,
                      boxShadow: selected
                        ? "0 4px 16px rgba(13,27,42,0.2)"
                        : "0 2px 8px rgba(0,0,0,0.06)",
                    } : {},
                  }}
                >
                  {/* Left accent */}
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                    bgcolor: selected ? "rgba(201,169,110,0.2)" : isFullHall ? `${T.gold}15` : T.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: selected ? T.gold : isFullHall ? T.gold : T.slateLt }}>
                      {opt.capacity}
                    </Typography>
                  </Box>

                  {/* Text */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: selected ? T.white : T.navy, lineHeight: 1.2 }}>
                      {opt.key}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.2, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 10, color: selected ? "rgba(255,255,255,0.5)" : T.slateLt }}>
                        {opt.capacity} guests {isFullHall && "· All venues"}
                      </Typography>
                      {capacityExceeded && (
                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.redTx }}>· Capacity exceeded</Typography>
                      )}
                      {pricingMap[opt.key] && !blocker && !capacityExceeded && (() => {
                        const p = pricingMap[opt.key];
                        if (guestCount > 0) {
                          const calc = calculateHallPrice({ flatFee: p.flatFee, perPersonPrice: p.pricePerPerson, guestCount, capacity: opt.capacity });
                          return (
                            <>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: selected ? T.gold : T.navy }}>
                                {fmtCAD(calc.total)}
                              </Typography>
                              <Typography sx={{ fontSize: 9, color: selected ? "rgba(255,255,255,0.4)" : T.slateLt }}>
                                {fmtCAD(p.flatFee)} + {fmtCAD(p.pricePerPerson)}/pp
                              </Typography>
                            </>
                          );
                        }
                        return (
                          <Typography sx={{ fontSize: 10, fontWeight: 600, color: selected ? T.gold : T.slate }}>
                            · {fmtCAD(p.flatFee)} + {fmtCAD(p.pricePerPerson)}/pp
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Badge / check */}
                  {(() => {
                    const recTag = recommendations.find(r => r.hallKey === opt.key)?.tag;
                    if (selected) return (
                      <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: T.gold, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: T.navy }} />
                      </Box>
                    );
                    if (recTag && !disabled) return (
                      <Box sx={{ bgcolor: recTag === "best_value" ? `${T.gold}18` : "#eff6ff", borderRadius: "6px", px: 1, py: "2px", flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 8, fontWeight: 700, color: recTag === "best_value" ? T.gold : "#3b82f6", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          {recTag === "best_value" ? "Best Fit" : "Cheapest"}
                        </Typography>
                      </Box>
                    );
                    return null;
                  })()}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </SectionCard>

      {/* ══════════ PRIMARY CUSTOMER ══════════ */}
      <SectionCard
        icon={<PeopleOutlineIcon sx={{ fontSize: 17 }} />}
        title="Primary Customer"
        subtitle="Contact details for this booking"
        action={
          <ToggleButtonGroup value={contactType} exclusive onChange={(_, v) => { if (v) setContactType(v); }} size="small" sx={{ height: 28, "& .MuiToggleButton-root": { border: `1px solid ${T.line}` } }}>
            <ToggleButton value="individual" sx={{ fontSize: 11, textTransform: "none", borderRadius: "8px 0 0 8px", px: 1.5, "&.Mui-selected": { bgcolor: T.peach, color: T.navy, borderColor: T.gold } }}>
              <PersonOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} /> Individual
            </ToggleButton>
            <ToggleButton value="corporate" sx={{ fontSize: 11, textTransform: "none", borderRadius: "0 8px 8px 0", px: 1.5, "&.Mui-selected": { bgcolor: T.peach, color: T.navy, borderColor: T.gold } }}>
              <BusinessIcon sx={{ fontSize: 14, mr: 0.5 }} /> Corporate
            </ToggleButton>
          </ToggleButtonGroup>
        }
      >
        {/* Search */}
        <Autocomplete
          freeSolo options={searchResults}
          getOptionLabel={opt => typeof opt === "string" ? opt : `${opt.customerFirstName} ${opt.customerLastName}`.trim()}
          inputValue={searchQuery} onInputChange={(_, val) => setSearchQuery(val)}
          onChange={(_, val) => { if (val && typeof val !== "string") handleSelectCustomer(val); }}
          loading={isSearching} noOptionsText={searchQuery.length < 2 ? "Type 2+ characters" : "No customers found"}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important", gap: "2px", py: "10px !important", "&:hover": { bgcolor: `${T.peach} !important` } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.navy }}>
                {option.customerFirstName} {option.customerLastName}
                {option.customerCompanyName && <Typography component="span" sx={{ fontSize: 11, color: T.slateLt, ml: 1 }}>({option.customerCompanyName})</Typography>}
              </Typography>
              <Typography sx={{ fontSize: 11, color: T.slateLt }}>{[option.customerEmail, option.customerMobileNum].filter(Boolean).join(" · ")}</Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField {...params} placeholder="Search existing customer..." size="small"
              InputProps={{ ...params.InputProps, startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.slateLt }} /></InputAdornment> }}
              sx={{ mb: 3, "& .MuiInputBase-root": { fontSize: 13, borderRadius: "10px", bgcolor: T.inputBg, border: `1px solid ${T.line}`, "&.Mui-focused": { bgcolor: T.peach, borderColor: T.gold }, "& fieldset": { border: "none" } } }}
            />
          )}
        />

        {/* Organization — full width, prominent */}
        <TextField
          label={contactType === "corporate" ? "Organization / Company Name *" : "Organization / Company Name"}
          fullWidth size="small" placeholder={contactType === "corporate" ? "Required for corporate bookings" : "Optional"}
          value={form.primaryCustomer.organization} onChange={setPrimary("organization")}
          error={contactType === "corporate" && !form.primaryCustomer.organization}
          sx={{ ...(contactType === "corporate" ? reqSx : inputSx), mb: 2.5, "& .MuiInputBase-root": { ...((contactType === "corporate" ? reqSx : inputSx) as any)["& .MuiInputBase-root"], fontSize: 14 } }}
        />

        {/* Name */}
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}><TextField label="First Name" fullWidth size="small" value={form.primaryCustomer.firstName} onChange={setPrimary("firstName")} sx={inputSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Last Name" fullWidth size="small" value={form.primaryCustomer.lastName} onChange={setPrimary("lastName")} sx={inputSx} /></Grid>
        </Grid>

        {/* Phone + Email */}
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}><TextField label="Phone" fullWidth size="small" value={form.primaryCustomer.phone1} onChange={setPhone("primaryCustomer", "phone1")} placeholder="647 667 1647" inputProps={{ maxLength: 12 }} error={!!phoneError(form.primaryCustomer.phone1)} helperText={phoneError(form.primaryCustomer.phone1)} sx={inputSx} /></Grid>
          <Grid item xs={12} sm={6}><TextField label="Email" type="email" fullWidth size="small" value={form.primaryCustomer.email1} onChange={setPrimary("email1")} placeholder="name@email.com" sx={inputSx} /></Grid>
        </Grid>

        {/* Divider + Actions */}
        <Box sx={{ height: 1, bgcolor: T.line, my: 3 }} />
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {actionBtn(showAddress ? "Hide Address" : "Add Address", <LocationOnOutlinedIcon sx={{ fontSize: 15 }} />, showAddress, () => setShowAddress(v => !v))}
          {actionBtn(showSecondary ? "Remove Contact" : "Secondary Contact", <PersonAddAltIcon sx={{ fontSize: 15 }} />, showSecondary, () => { setShowSecondary(v => !v); setForm(p => ({ ...p, hasSecondaryCustomer: !showSecondary })); })}
        </Box>

        {/* Address collapse */}
        <Collapse in={showAddress}>
          <Grid container spacing={2.5} sx={{ mt: 1.5 }}>
            <Grid item xs={12}>
              <AddressAutocomplete value={form.primaryCustomer.address}
                onChange={val => setForm(p => ({ ...p, primaryCustomer: { ...p.primaryCustomer, address: val } }))}
                onSelect={(parsed: ParsedAddress) => setForm(p => ({ ...p, primaryCustomer: { ...p.primaryCustomer, address: parsed.address, city: parsed.city || p.primaryCustomer.city, province: parsed.province || p.primaryCustomer.province, postalCode: parsed.postalCode || p.primaryCustomer.postalCode } }))}
                sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={5}><TextField label="City" fullWidth size="small" value={form.primaryCustomer.city} onChange={setPrimary("city")} sx={inputSx} /></Grid>
            <Grid item xs={6} sm={3}><TextField select label="Province" fullWidth size="small" value={form.primaryCustomer.province} onChange={setPrimary("province")} sx={inputSx}>{PROVINCES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6} sm={4}><TextField label="Postal Code" fullWidth size="small" value={form.primaryCustomer.postalCode} onChange={setPrimary("postalCode")} sx={inputSx} /></Grid>
          </Grid>
        </Collapse>

        {/* Secondary contact collapse */}
        <Collapse in={showSecondary}>
          <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${T.line}` }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>Secondary Contact</Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={4}><TextField label="First Name" fullWidth size="small" value={form.secondaryCustomer.firstName} onChange={setSecondary("firstName")} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Last Name" fullWidth size="small" value={form.secondaryCustomer.lastName} onChange={setSecondary("lastName")} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Organization" fullWidth size="small" value={form.secondaryCustomer.organization} onChange={setSecondary("organization")} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Phone" fullWidth size="small" value={form.secondaryCustomer.phone1} onChange={setPhone("secondaryCustomer", "phone1")} placeholder="647 667 1647" inputProps={{ maxLength: 12 }} error={!!phoneError(form.secondaryCustomer.phone1)} helperText={phoneError(form.secondaryCustomer.phone1)} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Email" type="email" fullWidth size="small" value={form.secondaryCustomer.email1} onChange={setSecondary("email1")} sx={inputSx} /></Grid>
            </Grid>
          </Box>
        </Collapse>
      </SectionCard>

      {/* ══════════ NOTES ══════════ */}
      <SectionCard icon={<NotesOutlinedIcon sx={{ fontSize: 17 }} />} title="Notes" subtitle="Additional information for this booking">
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField label="Public Notes" helperText="Visible on contract" fullWidth size="small" multiline rows={4} value={form.publicNotes} onChange={e => setForm(p => ({ ...p, publicNotes: e.target.value }))} sx={inputSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Private Notes" helperText="Internal use only" fullWidth size="small" multiline rows={4} value={form.privateNotes} onChange={e => setForm(p => ({ ...p, privateNotes: e.target.value }))} sx={inputSx} />
          </Grid>
        </Grid>
      </SectionCard>
    </Box>
  );
};

export default Step1EventDetails;
